import React from "react";
import {
  ComponentEnum,
  ComponentI,
  isSupportedComponentType,
} from "../../../types";
import VideoComponent from "../components/contentComponents/VideoComponent";
import TextComponent from "../components/contentComponents/TextComponent";
import ImageComponent from "../components/contentComponents/ImageComponent";
import MultipleChoiceComponent from "../components/contentComponents/MultipleChoiceComponent";
import ToggleTextComponent from "../components/contentComponents/ToggleTextComponent";
import RatingScaleComponent from "../components/contentComponents/RatingScaleComponent";
import LessonLinkComponent from "../components/contentComponents/LessonLinkComponent";
import CustomLinkComponent from "../components/contentComponents/CustomLinkComponent";
import PriorityListComponent from "../components/contentComponents/PriorityListComponent";
import Input from "../../../componentLibrary/Input";
import Textarea from "../../../componentLibrary/Textarea";
import UserListComponent from "../components/contentComponents/UserListComponent";
import DateTimeComponent from "../components/contentComponents/DateTimeComponent";
import ForEachComponent from "../components/contentComponents/ForEachComponent";
import CalloutBoxComponent from "../components/contentComponents/CalloutBoxComponent";
import ToggleComponent from "../components/contentComponents/ToggleComponent";
import AddToCalendarComponent from "../components/contentComponents/AddToCalendarComponent";
import { evaluateLogic } from "./logicEvaluator";
import { parsePreviousAnswers } from "./parsePreviousAnswers";
// Wrapper component for logic-based components
const LogicComponentWrapper = ({ children }) => (
  <div className="rounded-lg p-2 sm:p-4 bg-gray-100 space-y-3 sm:space-y-4">
    {children}
  </div>
);

interface RenderContentProps {
  component: ComponentI;
  index: number;
  pageIndex?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allResponses?: Record<string, any>;
  onChange?: (
    variable: string,
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    displayAs?: string,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalProps?: {
    hideComponents?: ComponentEnum[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ALL?: Record<string, any>;
  } & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key in ComponentEnum]?: Record<string, any>;
  };
  inputType?: "input" | "textarea";
  isLogicComponent?: boolean;
  key?: string;
}

export const renderContent = ({
  component,
  index,
  pageIndex = 0,
  value,
  allResponses = {},
  onChange,
  additionalProps = {},
  inputType = "input",
  isLogicComponent = false,
  key,
}: RenderContentProps) => {
  const componentKey = key || `${pageIndex}_${component.type}_${index}`;
  // Skip rendering components that are not in this build's enum (e.g. stage-only types when running in prod)
  if (!isSupportedComponentType(component.type)) {
    return null;
  }
  // Skip rendering components that are in the hideComponents list
  if (additionalProps.hideComponents?.includes(component.type)) {
    return null;
  }

  const getInputValue = (variable: string) => {
    if (!value) return "";
    return value[variable]?.value || "";
  };

  const handleInputChange = (
    variable: string,
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValue: any,
    displayAs?: string,
  ) => {
    if (!onChange) return;

    if (
      newValue === null ||
      newValue === "" ||
      (Array.isArray(newValue) &&
        (newValue.length === 0 ||
          (newValue.length === 1 && newValue[0] === "")))
    ) {
      onChange(variable, label, undefined);
    } else {
      onChange(variable, label, newValue, displayAs);
    }
  };

  // Helper function to handle spreading props for components
  const getComponentProps = (componentType) => ({
    ...additionalProps[componentType],
    ...additionalProps["ALL"],
  });

  const parseMCQOptions = (component: ComponentI) => {
    const options = Object.keys(component.content)
      .filter((key) => key.startsWith("choice_"))
      .map((key) => parsePreviousAnswers(component.content[key], allResponses));

    const linkedOptions =
      component.content.linkedVariable?.flatMap((variable) => {
        const response = allResponses[variable];
        if (!response?.value) return [];
        return Array.isArray(response.value)
          ? response.value
          : [response.value];
      }) || [];

    // Deduplicate options to prevent duplicates when the same value appears
    // in both choice_* fields and linkedVariable responses
    const allOptions = [...options, ...linkedOptions];
    const seen = new Set<string>();
    return allOptions.filter((option) => {
      if (seen.has(option)) {
        return false;
      }
      seen.add(option);
      return true;
    });
  };

  const parsePriorityListChoices = (
    component: ComponentI,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    allResponses1: Record<string, any>,
  ) => {
    const options = Object.keys(component.content)
      .filter((key) => key.startsWith("choice_"))
      .map((key) =>
        parsePreviousAnswers(component.content[key], allResponses1),
      );

    const linkedOptions =
      component.content.linkedVariable?.flatMap((variable) => {
        const response = allResponses1[variable];
        if (!response?.value) return [];
        return Array.isArray(response.value)
          ? response.value
          : [response.value];
      }) || [];

    // Deduplicate options to prevent duplicates when the same value appears
    // in both choice_* fields and linkedVariable responses
    const allOptions = [...options, ...linkedOptions];
    const seen = new Set<string>();
    return allOptions.filter((option) => {
      if (seen.has(option)) {
        return false;
      }
      seen.add(option);
      return true;
    });
  };

  const componentMap = {
    [ComponentEnum.VIDEO]: () => (
      <div key={componentKey} className="px-0 sm:px-2">
        <VideoComponent
          url={component.content.url}
          title={component.content.title}
          transcript={component.content.transcript}
          {...getComponentProps(ComponentEnum.VIDEO)}
        />
      </div>
    ),
    [ComponentEnum.TEXT]: () => (
      <div key={componentKey} className="px-0 sm:px-2">
        <TextComponent
          text={parsePreviousAnswers(component.content.text, allResponses)}
          {...getComponentProps(ComponentEnum.TEXT)}
        />
      </div>
    ),
    [ComponentEnum.INPUT]: () => {
      const InputComponent = inputType === "textarea" ? Textarea : Input;
      const componentProps = getComponentProps(ComponentEnum.INPUT);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { savePageInput: _unused, ...otherProps } = componentProps;

      return (
        <InputComponent
          key={componentKey}
          label={parsePreviousAnswers(component.content.label, allResponses)}
          placeholder={parsePreviousAnswers(
            component.content.placeholder,
            allResponses,
          )}
          value={getInputValue(component.content.variable)}
          convertPlaceholder={component.content.convertPlaceholder !== false}
          onChange={(event) =>
            handleInputChange(
              component.content.variable,
              component.content.label,
              event.target.value,
            )
          }
          className="px-4"
          isInteger={component.content.integerOnly === true}
          {...otherProps}
        />
      );
    },

    [ComponentEnum.IMAGE]: () => (
      <ImageComponent
        key={componentKey}
        imageId={component.content.imageId}
        width={component.content.width}
        hasBorder={component.content.hasBorder}
        {...getComponentProps(ComponentEnum.IMAGE)}
      />
    ),
    [ComponentEnum.MULTIPLE_CHOICE]: () => (
      <MultipleChoiceComponent
        key={componentKey}
        content={{
          label: parsePreviousAnswers(component.content.label, allResponses),
          choices: parseMCQOptions(component),
          other: component.content.other,
          msq: component.content.msq,
          linkedVariable: component.content.linkedVariable || [],
        }}
        value={getInputValue(component.content.variable)}
        onChange={(val) =>
          handleInputChange(
            component.content.variable,
            component.content.label,
            val,
          )
        }
        {...getComponentProps(ComponentEnum.MULTIPLE_CHOICE)}
      />
    ),
    [ComponentEnum.RATING_SCALE]: () => (
      <RatingScaleComponent
        key={componentKey}
        content={{
          label: parsePreviousAnswers(component.content.label, allResponses),
          minimumLabel: parsePreviousAnswers(
            component.content.minimumLabel,
            allResponses,
          ),
          maximumLabel: parsePreviousAnswers(
            component.content.maximumLabel,
            allResponses,
          ),
          minimumValue: component.content.minimumValue,
          maximumValue: component.content.maximumValue,
          numberOfSteps: component.content.numberOfSteps,
          centerLabel: parsePreviousAnswers(
            component.content.centerLabel,
            allResponses,
          ),
        }}
        value={getInputValue(component.content.variable)}
        onChange={(val) =>
          handleInputChange(
            component.content.variable,
            component.content.label,
            val,
          )
        }
        {...getComponentProps(ComponentEnum.RATING_SCALE)}
      />
    ),
    [ComponentEnum.TOGGLE_TEXT]: () => (
      <ToggleTextComponent
        key={componentKey}
        title={parsePreviousAnswers(component.content.title, allResponses)}
        body={parsePreviousAnswers(component.content.body, allResponses)}
        {...getComponentProps(ComponentEnum.TOGGLE_TEXT)}
      />
    ),
    [ComponentEnum.LESSON_LINK]: () => (
      <LessonLinkComponent
        key={componentKey}
        lesson_id={component.content.lesson_id}
        module_id={component.content.module_id}
        page_id={component.content.page_id}
        label={component.content.label}
        className="mx-0 sm:mx-4"
        {...getComponentProps(ComponentEnum.LESSON_LINK)}
      />
    ),
    [ComponentEnum.USER_LIST]: () => {
      const rawValue = getInputValue(component.content.variable);
      const valueArray = Array.isArray(rawValue)
        ? rawValue
        : typeof rawValue === "string"
          ? [rawValue]
          : rawValue?.value
            ? Array.isArray(rawValue.value)
              ? rawValue.value
              : [rawValue.value]
            : [];

      return (
        <UserListComponent
          key={componentKey}
          content={component.content}
          value={valueArray}
          onChange={(val, displayAs) =>
            handleInputChange(
              component.content.variable,
              component.content.label,
              val,
              displayAs,
            )
          }
          allResponses={allResponses}
          convertPlaceholder={component.content.convertPlaceholder === true}
          {...getComponentProps(ComponentEnum.USER_LIST)}
        />
      );
    },
    [ComponentEnum.CUSTOM_LINK]: () => (
      <CustomLinkComponent
        key={componentKey}
        button_text={component.content.button_text}
        url={component.content.url}
        url_type={component.content.url_type}
        icon_url={component.content.icon_url}
        icon_alt={component.content.icon_alt}
        className="mx-0 sm:mx-4"
        {...getComponentProps(ComponentEnum.CUSTOM_LINK)}
      />
    ),

    [ComponentEnum.PRIORITY_LIST]: () => {
      const onRefAssign =
        additionalProps[ComponentEnum.PRIORITY_LIST]?.onRefAssign;
      return (
        <PriorityListComponent
          key={componentKey}
          ref={(ref) => {
            if (onRefAssign) {
              onRefAssign(component._id, ref);
            }
          }}
          linkedVariable={component.content.linkedVariable || []}
          label={parsePreviousAnswers(component.content.label, allResponses)}
          variable={component.content.variable}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          parsePriorityListChoices={(allResponses1?: Record<string, any>) =>
            parsePriorityListChoices(component, allResponses1 || allResponses)
          }
          value={getInputValue(component.content.variable) || []}
          onChange={(val, displayAs) =>
            handleInputChange(
              component.content.variable,
              component.content.label,
              val,
              displayAs,
            )
          }
          {...getComponentProps(ComponentEnum.PRIORITY_LIST)}
        />
      );
    },
    [ComponentEnum.DATE_TIME]: () => (
      <DateTimeComponent
        key={componentKey}
        label={parsePreviousAnswers(component.content.label, allResponses)}
        type={component.content.type}
        onChange={(val, displayAs) =>
          handleInputChange(
            component.content.variable,
            component.content.label,
            val,
            displayAs,
          )
        }
        value={getInputValue(component.content.variable)}
        {...getComponentProps(ComponentEnum.DATE_TIME)}
      />
    ),
    [ComponentEnum.CALLOUT_BOX]: () => (
      <CalloutBoxComponent
        key={componentKey}
        word={parsePreviousAnswers(component.content.word, allResponses)}
        definition={parsePreviousAnswers(
          component.content.definition,
          allResponses,
        )}
        imageSrc={component.content.image_src}
        imageAlt={component.content.image_alt}
        imageWidth={component.content.image_width}
        hasBorder={component.content.image_hasBorder}
        {...getComponentProps(ComponentEnum.CALLOUT_BOX)}
      />
    ),
    [ComponentEnum.TOGGLE]: () => (
      <ToggleComponent
        key={componentKey}
        title={parsePreviousAnswers(component.content.title, allResponses)}
        componentsList={component.content.componentsList || []}
        allResponses={allResponses}
        onChange={onChange}
        additionalProps={additionalProps}
        pageIndex={pageIndex}
        inputType={inputType}
        value={value}
        {...getComponentProps(ComponentEnum.TOGGLE)}
      />
    ),
    [ComponentEnum.ADD_TO_CALENDAR]: () => (
      <AddToCalendarComponent
        key={componentKey}
        description={parsePreviousAnswers(
          component.content.description,
          allResponses,
        )}
        eventTitle={parsePreviousAnswers(
          component.content.eventTitle,
          allResponses,
        )}
        recurrence={component.content.recurrence}
        startTime={component.content.startTime}
        endTime={component.content.endTime}
        addBacklink={component.content.addBacklink !== false}
        {...getComponentProps(ComponentEnum.ADD_TO_CALENDAR)}
      />
    ),
  };

  const renderedComponent = componentMap[component.type]?.();
  if (!renderedComponent) return null;

  return isLogicComponent ? (
    <LogicComponentWrapper>{renderedComponent}</LogicComponentWrapper>
  ) : (
    renderedComponent
  );
};

export const renderPageComponents = (
  components: ComponentI[],
  pageIndex: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allResponses: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (
    variable: string,
    label: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    displayAs?: string,
  ) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalProps: Record<string, any> = {},
  inputType: "input" | "textarea" = "input",
) => {
  let index = 0;

  return components.flatMap((component, componentIndex) => {
    if (component.type === ComponentEnum.LOGIC_COMPONENT) {
      // Add defensive checks for logic component
      if (!component.content?.logic?.rules) {
        index += component.content?.componentsList?.length || 0;
        return null;
      }

      const shouldRender =
        (component.content.display &&
          evaluateLogic(component.content.logic, allResponses)) ||
        (!component.content.display &&
          !evaluateLogic(component.content.logic, allResponses));

      if (!shouldRender) {
        index += component.content?.componentsList?.length || 0;
        return null;
      }

      // Render all components from the same logic component in a single wrapper
      const renderedComponents = component.content.componentsList?.map(
        (componentFromList: ComponentI) => {
          const result = renderContent({
            component: componentFromList,
            index,
            pageIndex,
            value,
            allResponses,
            onChange,
            additionalProps,
            inputType,
          });
          index += 1;
          return result;
        },
      );

      return renderedComponents?.length ? (
        <LogicComponentWrapper key={`logic_${pageIndex}_${componentIndex}`}>
          {renderedComponents}
        </LogicComponentWrapper>
      ) : null;
    } else if (component.type === ComponentEnum.FOR_EACH) {
      const onRefAssign = additionalProps[ComponentEnum.FOR_EACH]?.onRefAssign;
      return (
        <ForEachComponent
          key={`foreach_${pageIndex}_${componentIndex}`}
          ref={(ref) => {
            if (onRefAssign) {
              onRefAssign(component._id, ref);
            }
          }}
          component={component}
          allResponses={allResponses}
          onChange={onChange}
          additionalProps={additionalProps[ComponentEnum.FOR_EACH]}
          inputType={inputType}
          pageIndex={pageIndex}
        />
      );
    } else {
      const result = renderContent({
        component,
        index,
        pageIndex,
        value,
        allResponses,
        onChange,
        additionalProps,
        inputType,
      });
      index += 1;
      return result;
    }
  });
};
